import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, DollarSign, Loader2, Download, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Price Input component - defined OUTSIDE to prevent re-creation on each render
const PriceInput = React.memo(({ label, priceKey, quantity, value, onChange, hasDefault }) => (
  <div className="flex items-center gap-2 py-1 border-b border-green-50">
    <div className="flex-1 min-w-0">
      <span className="text-sm text-stone-600 truncate">{label}</span>
      {quantity !== null && quantity > 0 && (
        <span className="text-xs text-amber-600 ml-1 font-medium">(x{quantity})</span>
      )}
      {hasDefault && (
        <span className="text-xs text-blue-500 ml-1">(default)</span>
      )}
    </div>
    <div className="relative w-28">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
      <Input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(priceKey, e.target.value)}
        placeholder="0.00"
        className={`h-8 pl-6 text-sm text-right ${hasDefault ? 'bg-blue-50 border-blue-200' : ''}`}
      />
    </div>
  </div>
));

PriceInput.displayName = 'PriceInput';

// ============ DEFAULT CATEGORY DEFINITIONS ============

// Default Tool Categories for Yard Pricing
const DEFAULT_TOOL_CATEGORIES = {
  protective_gear: {
    label: 'Protective Gear',
    icon: '🧤',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    items: [
      { key: 'suits', label: 'Bee Suits', priceKey: 'suits_price' },
      { key: 'jackets', label: 'Jackets', priceKey: 'jackets_price' },
      { key: 'veils', label: 'Veils', priceKey: 'veils_price' },
      { key: 'gloves', label: 'Gloves', priceKey: 'gloves_price' },
    ]
  },
  hive_tools: {
    label: 'Hive Tools',
    icon: '🔧',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    items: [
      { key: 'hive_tools', label: 'Hive Tools', priceKey: 'hive_tools_price' },
      { key: 'smokers', label: 'Smokers', priceKey: 'smokers_price' },
      { key: 'bee_brushes', label: 'Bee Brushes', priceKey: 'bee_brushes_price' },
      { key: 'frame_grips', label: 'Frame Grips', priceKey: 'frame_grips_price' },
      { key: 'frame_holders', label: 'Frame Holders', priceKey: 'frame_holders_price' },
      { key: 'queen_catchers', label: 'Queen Catchers', priceKey: 'queen_catchers_price' },
    ]
  },
  feeding_equipment: {
    label: 'Feeding Equipment',
    icon: '🍯',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    items: [
      { key: 'entrance_feeders', label: 'Entrance Feeders', priceKey: 'yard_entrance_feeders_price' },
      { key: 'hive_top_feeders', label: 'Hive Top Feeders', priceKey: 'yard_hive_top_feeders_price' },
      { key: 'top_feeders', label: 'Top Feeders', priceKey: 'yard_top_feeders_price' },
      { key: 'frame_feeders', label: 'Frame Feeders', priceKey: 'yard_frame_feeders_price' },
      { key: 'water_feeders', label: 'Water Feeders', priceKey: 'yard_water_feeders_price' },
    ]
  },
  heavy_equipment: {
    label: 'Heavy Equipment',
    icon: '📦',
    bg: 'bg-stone-100',
    border: 'border-stone-300',
    text: 'text-stone-800',
    items: [
      { key: 'solid_bottom_boards', label: 'Solid Bottom Boards', priceKey: 'solid_bottom_boards_price' },
      { key: 'slatted_racks', label: 'Slatted Racks', priceKey: 'slatted_racks_price' },
      { key: 'hive_stands', label: 'Hive Stands', priceKey: 'hive_stands_price' },
      { key: 'hive_scales', label: 'Hive Scales', priceKey: 'hive_scales_price' },
      { key: 'robbing_screens', label: 'Robbing Screens', priceKey: 'robbing_screens_price' },
      { key: 'ventilated_covers', label: 'Ventilated Covers', priceKey: 'ventilated_covers_price' },
      { key: 'winter_wraps', label: 'Winter Wraps', priceKey: 'winter_wraps_price' },
      { key: 'pollen_traps', label: 'Pollen Traps', priceKey: 'pollen_traps_price' },
      { key: 'vapourisers', label: 'Vapourisers', priceKey: 'vapourisers_price' },
      { key: 'venom_traps', label: 'Venom Traps', priceKey: 'venom_traps_price' },
    ]
  },
  miscellaneous: {
    label: 'Miscellaneous',
    icon: '📋',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-800',
    items: [
      { key: 'diatomaceous_earth', label: 'Diatomaceous Earth', priceKey: 'diatomaceous_earth_price' },
      { key: 'rake', label: 'Rake', priceKey: 'rake_price' },
      { key: 'spade', label: 'Spade', priceKey: 'spade_price' },
    ]
  },
};

// Default Component Categories for Hive Pricing
const DEFAULT_COMPONENT_CATEGORIES = {
  hive_types: {
    label: 'Hive Types',
    icon: '📦',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    items: [
      { key: 'langstroth_deep_brood_qty', label: 'Langstroth Deep Brood Box', priceKey: 'langstroth_deep_brood_price' },
      { key: 'langstroth_deep_super_qty', label: 'Langstroth Deep Super', priceKey: 'langstroth_deep_super_price' },
      { key: 'langstroth_ideal_super_qty', label: 'Langstroth Ideal Super', priceKey: 'langstroth_ideal_super_price' },
      { key: 'flow_deep_brood_qty', label: 'Flow Hive Brood Box', priceKey: 'flow_hive_brood_price' },
      { key: 'flow_super_qty', label: 'Flow Hive Super', priceKey: 'flow_hive_super_price' },
      { key: 'national_deep_qty', label: 'National Deep Box', priceKey: 'national_deep_price' },
      { key: 'national_shallow_qty', label: 'National Shallow Box', priceKey: 'national_shallow_price' },
      { key: 'keepers_medium_nuc_qty', label: 'Keepers Medium NUC', priceKey: 'keepers_medium_nuc_price' },
      { key: 'keepers_langstroth_deep_qty', label: 'Keepers Langstroth Deep', priceKey: 'keepers_langstroth_deep_price' },
      { key: 'keepers_super_deep_qty', label: 'Keepers Super Deep', priceKey: 'keepers_super_deep_price' },
      { key: 'warre_boxes', label: 'Warre Box', priceKey: 'warre_box_price' },
      { key: null, label: 'Top Bar Hive Body', priceKey: 'top_bar_hive_price' },
      { key: null, label: 'Horizontal Hive Body', priceKey: 'horizontal_hive_price' },
      { key: null, label: 'Layens Hive Body', priceKey: 'layens_hive_price' },
      { key: null, label: 'NUC Box', priceKey: 'nuc_box_price' },
      { key: null, label: 'Queen Mating Box', priceKey: 'queen_mating_box_price' },
      { key: null, label: 'Other Hive Body/Custom', priceKey: 'other_hive_body_price' },
    ]
  },
  frames: {
    label: 'Frames & Foundation',
    icon: '🖼️',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    items: [
      { key: null, label: 'Deep Frames (each)', priceKey: 'deep_frames_price' },
      { key: null, label: 'Medium Frames (each)', priceKey: 'medium_frames_price' },
      { key: null, label: 'Shallow Frames (each)', priceKey: 'shallow_frames_price' },
      { key: null, label: 'Foundationless Frames (each)', priceKey: 'foundationless_frames_price' },
      { key: null, label: 'Wax Foundation (each)', priceKey: 'wax_foundation_price' },
      { key: null, label: 'Plastic Foundation (each)', priceKey: 'plastic_foundation_price' },
      { key: null, label: 'Flow Frames (set)', priceKey: 'flow_frames_price' },
    ]
  },
  lids_covers: {
    label: 'Lids & Covers',
    icon: '🏠',
    bg: 'bg-stone-100',
    border: 'border-stone-300',
    text: 'text-stone-800',
    items: [
      { key: 'migration_lid', label: 'Migration Lid', priceKey: 'migration_lid_price' },
      { key: 'telescopic_lid', label: 'Telescopic Lid', priceKey: 'telescopic_lid_price' },
      { key: 'inner_cover', label: 'Inner Cover', priceKey: 'inner_cover_price' },
      { key: 'crown_board', label: 'Crown Board', priceKey: 'crown_board_price' },
    ]
  },
  bottom_boards: {
    label: 'Bottom Boards & Stands',
    icon: '⬇️',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    items: [
      { key: 'screened_bottom_board', label: 'Screened Bottom Board', priceKey: 'screened_bottom_board_price' },
      { key: 'screened_bottom_board_with_tray', label: 'Screened BB with Tray', priceKey: 'screened_bottom_board_tray_price' },
      { key: 'solid_bottom_board', label: 'Solid Bottom Board', priceKey: 'solid_bottom_board_price' },
      { key: 'slatted_rack', label: 'Slatted Rack', priceKey: 'slatted_rack_price' },
      { key: 'hive_stand_qty', label: 'Hive Stand', priceKey: 'hive_stand_price' },
    ]
  },
  feeders: {
    label: 'Feeders',
    icon: '🍯',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-800',
    items: [
      { key: 'top_feeder', label: 'Top Feeder', priceKey: 'top_feeder_price' },
      { key: 'frame_feeder', label: 'Frame Feeder', priceKey: 'frame_feeder_price' },
      { key: 'entrance_feeder', label: 'Entrance Feeder', priceKey: 'entrance_feeder_price' },
    ]
  },
  queen_management: {
    label: 'Queen & Brood',
    icon: '👑',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-800',
    items: [
      { key: 'queen_excluder', label: 'Queen Excluder', priceKey: 'queen_excluder_price' },
      { key: 'bee_escape', label: 'Bee Escape', priceKey: 'bee_escape_price' },
      { key: null, label: 'Queen Catcher', priceKey: 'queen_catcher_price' },
      { key: null, label: 'Queen Cage', priceKey: 'queen_cage_price' },
    ]
  },
  pest_control: {
    label: 'Pest Control',
    icon: '🐛',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    items: [
      { key: 'beetle_trap', label: 'Beetle Trap', priceKey: 'beetle_trap_price' },
      { key: 'mouse_guard', label: 'Mouse Guard', priceKey: 'mouse_guard_price' },
      { key: 'entrance_reducer', label: 'Entrance Reducer', priceKey: 'entrance_reducer_price' },
      { key: 'robbing_screen_qty', label: 'Robbing Screen', priceKey: 'robbing_screen_price' },
      { key: 'propolis_sheet', label: 'Propolis Sheet', priceKey: 'propolis_sheet_price' },
    ]
  },
  heavy_accessories: {
    label: 'Heavy & Accessories',
    icon: '⚙️',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-800',
    items: [
      { key: 'hive_scale_qty', label: 'Hive Scale', priceKey: 'hive_scale_price' },
      { key: 'straps', label: 'Hive Straps', priceKey: 'straps_price' },
      { key: 'winter_wrap_qty', label: 'Winter Wrap', priceKey: 'winter_wrap_price' },
      { key: 'pollen_trap_qty', label: 'Pollen Trap', priceKey: 'pollen_trap_price' },
      { key: 'fume_board', label: 'Fume Board', priceKey: 'fume_board_price' },
      { key: 'vapouriser_qty', label: 'Vapouriser', priceKey: 'vapouriser_price' },
      { key: 'venom_trap_qty', label: 'Venom Trap', priceKey: 'venom_trap_price' },
      { key: 'ventilated_cover_qty', label: 'Ventilated Cover', priceKey: 'ventilated_cover_price' },
    ]
  },
};

// Custom items for both
const CUSTOM_FIELDS = [
  { key: null, label: 'Custom Items Total', priceKey: 'custom_items_total' },
  { key: null, label: 'Shipping/Delivery', priceKey: 'shipping_price' },
  { key: null, label: 'Assembly/Labor', priceKey: 'labor_price' },
  { key: null, label: 'Other Costs', priceKey: 'other_costs' },
];

// Comprehensive pricing component for all hive/yard items
const PricingSection = ({ 
  pricing = {}, 
  onPricingChange, 
  showOriginPrice = false,
  originPrice = 0,
  onOriginPriceChange,
  stackConfig = {},
  components = {},
  tools = {},
  miscellaneous = {},
  isYardPricing = false, // true for yard dialogs, false for hive dialogs
  adminTools = [], // Admin-configured custom tools (legacy prop)
  adminComponents = [], // Admin-configured custom components (legacy prop)
  defaultPrices = {}, // Default prices from Hive Costs page
  frameTypes = [], // Selected frame types
  frameQuantities = {}, // Frame quantities by type
  foundationTypes = [], // Selected foundation types
  foundationQuantities = {}, // Foundation quantities by type
  entityName = '' // Name of the hive or apiary for CSV export
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [toolsConfig, setToolsConfig] = useState({ items: [], custom_categories: [] });
  const [componentsConfig, setComponentsConfig] = useState({ items: [], custom_categories: [] });
  const hasDefaults = Object.keys(defaultPrices).length > 0;
  
  // Use ref to track latest pricing to avoid stale closures
  const pricingRef = useRef(pricing);
  useEffect(() => {
    pricingRef.current = pricing;
  }, [pricing]);

  // Fetch configuration on mount (not just on expand) so costs are ready
  useEffect(() => {
    fetchConfiguration();
  }, []);

  // Set default active tab once we know which mode we're in
  useEffect(() => {
    if (activeTab === null) {
      setActiveTab(isYardPricing ? 'protective_gear' : 'hive_types');
    }
  }, [isYardPricing, activeTab]);

  const fetchConfiguration = async () => {
    setConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [toolsRes, componentsRes, toolCategoriesRes, componentCategoriesRes] = await Promise.all([
        fetch(`${API}/config/admin_tools`, { headers }),
        fetch(`${API}/config/admin_components`, { headers }),
        fetch(`${API}/config/custom-categories/tools`, { headers }),
        fetch(`${API}/config/custom-categories/components`, { headers })
      ]);
      
      if (toolsRes.ok) {
        const toolsData = await toolsRes.json();
        const toolCategoriesData = toolCategoriesRes.ok ? await toolCategoriesRes.json() : [];
        // Handle both array and {items: []} response formats
        setToolsConfig({
          items: Array.isArray(toolsData) ? toolsData : (toolsData.items || []),
          custom_categories: toolCategoriesData || []
        });
      }
      
      if (componentsRes.ok) {
        const componentsData = await componentsRes.json();
        const componentCategoriesData = componentCategoriesRes.ok ? await componentCategoriesRes.json() : [];
        setComponentsConfig({
          items: Array.isArray(componentsData) ? componentsData : (componentsData.items || []),
          custom_categories: componentCategoriesData || []
        });
      }
    } catch (error) {
    } finally {
      setConfigLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

  // Stable callback for price changes using ref
  const handlePriceChange = useCallback((key, value) => {
    const numValue = value === '' ? '' : (parseFloat(value) || 0);
    const newPricing = { ...pricingRef.current, [key]: numValue };
    onPricingChange(newPricing);
  }, [onPricingChange]);

  // Calculate totals - use dynamic tabs from DB
  const calculateTotal = () => {
    let total = parseFloat(originPrice || 0);
    
    const tabs = isYardPricing ? buildToolTabs() : buildComponentTabs();
    
    // Add prices for items with quantity > 0
    tabs.forEach(tab => {
      if (tab.isCustom || !tab.category) return;
      tab.category.items.forEach(item => {
        const qty = getQuantity(item.key);
        if (qty > 0) {
          const savedPrice = pricing[item.priceKey];
          const defaultPrice = defaultPrices[item.priceKey];
          const priceToUse = savedPrice !== undefined && savedPrice !== '' ? savedPrice : defaultPrice;
          if (priceToUse) {
            total += parseFloat(priceToUse || 0) * qty;
          }
        }
      });
    });
    
    // Mapping from frame type to default price key
    const frameTypeToDefaultKey = {
      'Wooden': 'deep_frames_price',
      'Plastic': 'plastic_frames_price',
      'Foundationless': 'foundationless_frames_price',
      'Flow': 'flow_frames_price',
      'Medium': 'medium_frames_price',
      'Shallow': 'shallow_frames_price',
    };
    
    // Mapping from foundation type to default price key  
    const foundationTypeToDefaultKey = {
      'Wax': 'wax_foundation_price',
      'Plastic': 'plastic_foundation_price',
    };
    
    // Add frame prices (from selected frame types that have quantity)
    frameTypes.forEach(frameType => {
      const qty = frameQuantities[frameType] || 0;
      if (qty > 0) {
        const priceKey = `frame_${frameType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const savedPrice = pricing[priceKey];
        const defaultKey = frameTypeToDefaultKey[frameType] || priceKey;
        const defaultPrice = defaultPrices[priceKey] || defaultPrices[defaultKey];
        const priceToUse = savedPrice !== undefined && savedPrice !== '' ? savedPrice : defaultPrice;
        if (priceToUse) {
          // Multiply by quantity for frames
          total += parseFloat(priceToUse || 0) * qty;
        }
      }
    });
    
    // Add foundation prices (from selected foundation types that have quantity)
    foundationTypes.forEach(foundationType => {
      const qty = foundationQuantities[foundationType] || 0;
      if (qty > 0) {
        const priceKey = `foundation_${foundationType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const savedPrice = pricing[priceKey];
        const defaultKey = foundationTypeToDefaultKey[foundationType] || priceKey;
        const defaultPrice = defaultPrices[priceKey] || defaultPrices[defaultKey];
        const priceToUse = savedPrice !== undefined && savedPrice !== '' ? savedPrice : defaultPrice;
        if (priceToUse) {
          // Multiply by quantity for foundations
          total += parseFloat(priceToUse || 0) * qty;
        }
      }
    });
    
    // Add custom fields that have values
    CUSTOM_FIELDS.forEach(field => {
      const savedPrice = pricing[field.priceKey];
      if (savedPrice && parseFloat(savedPrice) > 0) {
        total += parseFloat(savedPrice);
      }
    });
    
    return total;
  };

  // Get quantity value from appropriate source
  const getQuantity = (qtyKey) => {
    if (!qtyKey) return null;
    // For yard pricing, prioritize tools; for hive pricing, prioritize components
    if (isYardPricing) {
      return tools[qtyKey] || (miscellaneous[qtyKey] ? 1 : 0) || null;
    }
    return stackConfig[qtyKey] || components[qtyKey] || null;
  };

  // Export pricing to CSV with proper headings
  const handleExportPricing = () => {
    const tabs = isYardPricing ? buildToolTabs() : buildComponentTabs();
    const csvEntityName = (entityName || (isYardPricing ? 'Apiary' : 'Hive')).replace(/[^\x20-\x7E]/g, '-');
    const reportType = isYardPricing ? 'Apiary Tools & Equipment Pricing' : 'Hive Components Pricing';
    
    const rows = [
      [`${csvEntityName} — ${reportType} Pricing`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Category', 'Item', 'Qty', 'Unit Price', 'Subtotal'],
    ];
    
    let grandTotal = 0;
    
    // Add origin/purchase price if present
    if (showOriginPrice && parseFloat(originPrice || 0) > 0) {
      const op = parseFloat(originPrice);
      rows.push(['Origin', 'Purchase / Origin Price', 1, op, op]);
      grandTotal += op;
    }
    
    tabs.forEach(tab => {
      if (tab.isCustom || !tab.category) return;
      tab.category.items.forEach(item => {
        const qty = getQuantity(item.key) || 0;
        if (qty > 0) {
          const price = parseFloat(pricing[item.priceKey] || defaultPrices[item.priceKey] || 0);
          const subtotal = qty * price;
          rows.push([tab.category.label, item.label, qty, price || '', subtotal || '']);
          grandTotal += subtotal;
        }
      });
    });
    
    // Add frame prices
    const frameTypeToDefaultKey = {
      'Wooden': 'deep_frames_price',
      'Plastic': 'plastic_frames_price',
      'Foundationless': 'foundationless_frames_price',
      'Flow': 'flow_frames_price',
      'Medium': 'medium_frames_price',
      'Shallow': 'shallow_frames_price',
    };
    frameTypes.forEach(frameType => {
      const qty = frameQuantities[frameType] || 0;
      if (qty > 0) {
        const priceKey = `frame_${frameType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const defaultKey = frameTypeToDefaultKey[frameType] || priceKey;
        const price = parseFloat(pricing[priceKey] ?? defaultPrices[priceKey] ?? defaultPrices[defaultKey] ?? 0);
        const subtotal = qty * price;
        rows.push(['Frames', `${frameType} Frames`, qty, price || '', subtotal || '']);
        grandTotal += subtotal;
      }
    });

    // Add foundation prices
    const foundationTypeToDefaultKey = {
      'Wax': 'wax_foundation_price',
      'Plastic': 'plastic_foundation_price',
    };
    foundationTypes.forEach(foundationType => {
      const qty = foundationQuantities[foundationType] || 0;
      if (qty > 0) {
        const priceKey = `foundation_${foundationType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const defaultKey = foundationTypeToDefaultKey[foundationType] || priceKey;
        const price = parseFloat(pricing[priceKey] ?? defaultPrices[priceKey] ?? defaultPrices[defaultKey] ?? 0);
        const subtotal = qty * price;
        rows.push(['Foundation', `${foundationType} Foundation`, qty, price || '', subtotal || '']);
        grandTotal += subtotal;
      }
    });

    // Add custom fields
    CUSTOM_FIELDS.forEach(field => {
      const price = parseFloat(pricing[field.priceKey] || 0);
      if (price > 0) {
        rows.push(['Custom', field.label, '', price, price]);
        grandTotal += price;
      }
    });
    
    rows.push([]);
    rows.push(['', '', '', 'TOTAL', grandTotal.toFixed(2)]);
    
    const csvContent = '\uFEFF' + rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = csvEntityName.replace(/[^a-zA-Z0-9]/g, '_');
    link.download = `${safeName}_pricing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Import pricing from CSV
  const handleImportPricing = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        const lines = text.split('\n').slice(1); // Skip header
        const newPricing = { ...pricingRef.current };
        let newOriginPrice = originPrice;
        
        lines.forEach(line => {
          if (!line.trim()) return;
          // Parse CSV line (handle quoted values)
          const match = line.match(/(?:"([^"]*)")|([^,]+)/g);
          if (match && match.length >= 4) {
            const priceKey = match[2]?.replace(/"/g, '').trim();
            const priceValue = match[3]?.replace(/"/g, '').trim();
            
            if (priceKey === 'origin_price') {
              newOriginPrice = parseFloat(priceValue) || 0;
              if (onOriginPriceChange) {
                onOriginPriceChange(newOriginPrice);
              }
            } else if (priceKey && priceValue !== '') {
              newPricing[priceKey] = parseFloat(priceValue) || 0;
            }
          }
        });
        
        onPricingChange(newPricing);
      } catch (err) {
        alert('Failed to import pricing. Please ensure the CSV file is in the correct format.');
      }
    };
    reader.readAsText(file);
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  // Build dynamic tabs for Yard pricing (Tools) - FULLY from admin DB
  const buildToolTabs = () => {
    const tabs = [];
    const toolsBySection = {};
    
    // Group ALL admin tools by section
    (toolsConfig.items || []).forEach(tool => {
      if (tool.is_active === false) return;
      const section = tool.section || 'hive_tools';
      if (!toolsBySection[section]) toolsBySection[section] = [];
      toolsBySection[section].push(tool);
    });
    
    const sectionLabels = {
      protective_gear: { label: 'Protective Gear', icon: '🦺', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
      hive_tools: { label: 'Hive Tools', icon: '🔧', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
      feeding_equipment: { label: 'Feeding Equipment', icon: '🍯', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
      harvesting_equipment: { label: 'Harvesting Equipment', icon: '🧴', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
      heavy_equipment: { label: 'Heavy Equipment', icon: '📦', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-800' },
      miscellaneous: { label: 'Miscellaneous', icon: '📋', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800' },
    };
    // Add custom category labels
    (toolsConfig.custom_categories || []).forEach(cat => {
      sectionLabels[cat.key] = { label: cat.name, icon: '✨', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' };
    });
    
    Object.entries(toolsBySection).forEach(([sectionKey, sectionTools]) => {
      const style = sectionLabels[sectionKey] || { label: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' };
      tabs.push({
        id: sectionKey,
        label: `${style.icon} ${style.label}`,
        category: {
          ...style,
          items: sectionTools.map(t => {
            const formKey = t.form_key || t.name.toLowerCase().replace(/\s+/g, '_');
            return {
              key: formKey,
              label: t.name,
              priceKey: `${formKey}_price`,
            };
          })
        },
        customItems: []
      });
    });
    
    // Always add custom tab at the end
    tabs.push({ id: 'custom', label: '✨ Custom', isCustom: true });
    return tabs;
  };

  // Build dynamic tabs for Hive pricing (Components) - FULLY from admin DB + structural
  const buildComponentTabs = () => {
    const tabs = [];
    const compsBySection = {};
    
    // Group ALL admin components by section
    (componentsConfig.items || []).forEach(comp => {
      if (comp.is_active === false) return;
      const section = comp.section || 'miscellaneous';
      if (!compsBySection[section]) compsBySection[section] = [];
      compsBySection[section].push(comp);
    });
    
    const sectionLabels = {
      lids_covers: { label: 'Lids & Covers', icon: '🏠', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-800' },
      bottom_boards_stands: { label: 'Bottom Boards & Stands', icon: '⬇️', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
      feeders: { label: 'Feeders', icon: '🍯', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
      queen_bee_management: { label: 'Queen Management', icon: '👑', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
      pest_control: { label: 'Pest Control', icon: '🛡️', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
      heavy_accessories: { label: 'Heavy & Accessories', icon: '⚙️', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800' },
      miscellaneous: { label: 'Miscellaneous', icon: '📋', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800' },
    };
    // Add custom category labels
    (componentsConfig.custom_categories || []).forEach(cat => {
      sectionLabels[cat.key] = { label: cat.name, icon: '✨', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800' };
    });
    
    // Add structural categories first (hive types, frames)
    const structuralKeys = ['hive_types', 'frames'];
    structuralKeys.forEach(key => {
      if (DEFAULT_COMPONENT_CATEGORIES[key]) {
        tabs.push({
          id: key,
          label: `${DEFAULT_COMPONENT_CATEGORIES[key].icon} ${DEFAULT_COMPONENT_CATEGORIES[key].label}`,
          category: DEFAULT_COMPONENT_CATEGORIES[key],
          customItems: []
        });
      }
    });
    
    // Add dynamic component sections from DB
    Object.entries(compsBySection).forEach(([sectionKey, sectionComps]) => {
      const style = sectionLabels[sectionKey] || { label: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' };
      tabs.push({
        id: sectionKey,
        label: `${style.icon} ${style.label}`,
        category: {
          ...style,
          items: sectionComps.map(c => {
            const formKey = c.form_key || c.name.toLowerCase().replace(/\s+/g, '_');
            return {
              key: formKey,
              label: c.name,
              priceKey: `${formKey}_price`,
            };
          })
        },
        customItems: []
      });
    });
    
    // Always add custom tab at the end
    tabs.push({ id: 'custom', label: '✨ Custom', isCustom: true });
    return tabs;
  };

  // Special render function for Frames & Foundation tab
  const renderFramesTab = () => {
    const frameItems = [];
    const foundationItems = [];
    
    // Mapping from frame type to default price key
    const frameTypeToDefaultKey = {
      'Wooden': 'deep_frames_price',
      'Plastic': 'plastic_frames_price',
      'Foundationless': 'foundationless_frames_price',
      'Flow': 'flow_frames_price',
      'Medium': 'medium_frames_price',
      'Shallow': 'shallow_frames_price',
    };
    
    // Mapping from foundation type to default price key  
    const foundationTypeToDefaultKey = {
      'Wax': 'wax_foundation_price',
      'Plastic': 'plastic_foundation_price',
    };
    
    // Build frame items from selected frame types
    frameTypes.forEach(frameType => {
      const qty = frameQuantities[frameType] || 0;
      if (qty > 0) {
        const priceKey = `frame_${frameType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const savedPrice = pricing[priceKey];
        // Try multiple possible default price keys
        const defaultKey = frameTypeToDefaultKey[frameType] || priceKey;
        const defaultPrice = defaultPrices[priceKey] || defaultPrices[defaultKey];
        frameItems.push({
          label: `${frameType} Frames`,
          priceKey,
          quantity: qty,
          savedPrice,
          defaultPrice,
          displayValue: savedPrice || defaultPrice || ''
        });
      }
    });
    
    // Build foundation items from selected foundation types
    foundationTypes.forEach(foundationType => {
      const qty = foundationQuantities[foundationType] || 0;
      if (qty > 0) {
        const priceKey = `foundation_${foundationType.toLowerCase().replace(/\s+/g, '_')}_price`;
        const savedPrice = pricing[priceKey];
        // Try multiple possible default price keys
        const defaultKey = foundationTypeToDefaultKey[foundationType] || priceKey;
        const defaultPrice = defaultPrices[priceKey] || defaultPrices[defaultKey];
        foundationItems.push({
          label: `${foundationType} Foundation`,
          priceKey,
          quantity: qty,
          savedPrice,
          defaultPrice,
          displayValue: savedPrice || defaultPrice || ''
        });
      }
    });
    
    const hasItems = frameItems.length > 0 || foundationItems.length > 0;
    
    return (
      <div className="space-y-1">
        {frameItems.length > 0 && (
          <>
            <p className="text-xs text-blue-600 font-medium mb-1">Frames:</p>
            {frameItems.map(item => (
              <PriceInput
                key={item.priceKey}
                label={item.label}
                priceKey={item.priceKey}
                quantity={item.quantity}
                value={item.displayValue}
                onChange={handlePriceChange}
                hasDefault={!item.savedPrice && item.defaultPrice}
              />
            ))}
          </>
        )}
        
        {foundationItems.length > 0 && (
          <>
            {frameItems.length > 0 && <div className="border-t border-blue-200 my-2 pt-2" />}
            <p className="text-xs text-blue-600 font-medium mb-1">Foundation:</p>
            {foundationItems.map(item => (
              <PriceInput
                key={item.priceKey}
                label={item.label}
                priceKey={item.priceKey}
                quantity={item.quantity}
                value={item.displayValue}
                onChange={handlePriceChange}
                hasDefault={!item.savedPrice && item.defaultPrice}
              />
            ))}
          </>
        )}
        
        {!hasItems && (
          <p className="text-sm text-stone-500 italic">No frames or foundation selected. Add them in the Frames & Foundation section above.</p>
        )}
      </div>
    );
  };

  const tabs = isYardPricing ? buildToolTabs() : buildComponentTabs();

  // Render fields for a tab
  const renderTabContent = (tab) => {
    if (tab.isCustom) {
      return renderCustomTab();
    }
    
    // Special handling for Frames & Foundation tab
    if (tab.id === 'frames') {
      return renderFramesTab();
    }
    
    const defaultItems = tab.category?.items || [];
    const customItems = tab.customItems || [];
    
    // Filter items to only show those that are actually used in the hive
    // (have quantity > 0)
    const activeDefaultItems = defaultItems.filter(item => {
      const qty = getQuantity(item.key);
      return qty && qty > 0;
    });
    
    const activeCustomItems = customItems.filter(item => {
      // Use name-based key matching EditHiveDialog's component key pattern
      const compKey = item.name.toLowerCase().replace(/\s+/g, '_');
      const qty = getQuantity(compKey);
      return qty && qty > 0;
    });
    
    return (
      <div className="space-y-1">
        {/* Default items - only show if they have quantity or price */}
        {activeDefaultItems.map(item => {
          // Use default price if item has quantity but no saved price
          const savedPrice = pricing[item.priceKey];
          const defaultPrice = defaultPrices[item.priceKey];
          const displayValue = savedPrice || (defaultPrice && getQuantity(item.key) > 0 ? defaultPrice : '');
          
          return (
            <PriceInput
              key={item.priceKey}
              label={item.label}
              priceKey={item.priceKey}
              quantity={getQuantity(item.key)}
              value={displayValue}
              onChange={handlePriceChange}
              hasDefault={!savedPrice && defaultPrice && getQuantity(item.key) > 0}
            />
          );
        })}
        
        {/* Custom items from config - only show if they have quantity or price */}
        {activeCustomItems.length > 0 && (
          <>
            {activeDefaultItems.length > 0 && <div className="border-t border-green-200 my-2 pt-2" />}
            <p className="text-xs text-green-600 font-medium mb-1">Custom Items:</p>
            {activeCustomItems.map(item => {
              const compKey = item.name.toLowerCase().replace(/\s+/g, '_');
              const priceKey = `${compKey}_price`;
              const savedPrice = pricing[priceKey];
              const defaultPrice = defaultPrices[priceKey];
              const displayValue = savedPrice || defaultPrice || item.default_price || '';
              
              return (
                <PriceInput
                  key={item.id || priceKey}
                  label={item.name}
                  priceKey={priceKey}
                  quantity={getQuantity(compKey)}
                  value={displayValue}
                  onChange={handlePriceChange}
                  hasDefault={!savedPrice && (defaultPrice || item.default_price)}
                />
              );
            })}
          </>
        )}
        
        {activeDefaultItems.length === 0 && activeCustomItems.length === 0 && (
          <p className="text-sm text-stone-500 italic">No items selected in this category. Add components in the hive configuration above.</p>
        )}
      </div>
    );
  };

  // Render custom tab content
  const renderCustomTab = () => {
    return (
      <>
        <p className="text-sm text-stone-500 mb-2">Add any custom/additional costs not covered in other tabs:</p>
        <div className="space-y-1">
          {CUSTOM_FIELDS.map(field => (
            <PriceInput
              key={field.priceKey}
              label={field.label}
              priceKey={field.priceKey}
              quantity={null}
              value={pricing[field.priceKey] || ''}
              onChange={handlePriceChange}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4 min-w-0 overflow-hidden">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          <Label className="text-stone-700 font-medium text-base cursor-pointer">
            💰 Hive Costs
          </Label>
          {hasDefaults && !isExpanded && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              Defaults loaded
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-green-700 font-semibold">
            Total: ${formatCurrency(calculateTotal())}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-stone-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-stone-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Defaults info banner */}
          {hasDefaults && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              💡 Default prices from <strong>Hive Costs</strong> are applied to selected items. Fields marked <span className="text-blue-500">(default)</span> show your preset values.
            </div>
          )}
          
          {/* Origin/Bee Purchase Price - Only for hives */}
          {showOriginPrice && !isYardPricing && (
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <Label className="text-sm font-medium text-stone-700 mb-2 block">🐝 Bee Purchase Price</Label>
              <div className="flex items-center gap-2">
                <span className="text-stone-500 text-sm flex-1">Cost paid for bees (package, nuc, swarm, etc.)</span>
                <div className="relative w-32">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={originPrice || ''}
                    onChange={(e) => onOriginPriceChange(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="h-9 pl-6 text-right"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {configLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-green-600 mr-2" />
              <span className="text-sm text-stone-500">Loading pricing options...</span>
            </div>
          )}

          {/* Tab Navigation */}
          {!configLoading && (
            <>
              <div className="flex flex-wrap gap-1 border-b border-green-200 pb-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTab(tab.id);
                    }}
                    className={`px-2 py-1.5 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      activeTab === tab.id 
                        ? 'bg-green-600 text-white' 
                        : 'text-stone-600 hover:bg-green-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="bg-white rounded-lg p-3 border border-green-100 max-h-64 overflow-y-auto">
                {tabs.find(t => t.id === activeTab) && renderTabContent(tabs.find(t => t.id === activeTab))}
              </div>
            </>
          )}

          {/* Import/Export Buttons */}
          <div className="flex gap-2 pt-2 border-t border-green-200">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExportPricing}
              className="flex-1 text-xs h-8 border-green-300 text-green-700 hover:bg-green-50"
            >
              <Download className="h-3 w-3 mr-1" />
              Export CSV
            </Button>
            <label className="flex-1">
              <input
                type="file"
                accept=".csv"
                onChange={handleImportPricing}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full text-xs h-8 border-green-300 text-green-700 hover:bg-green-50"
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.parentElement?.querySelector('input')?.click();
                }}
              >
                <Upload className="h-3 w-3 mr-1" />
                Import CSV
              </Button>
            </label>
          </div>

          {/* Total Summary */}
          <div className="bg-green-100 rounded-lg p-3 flex items-center justify-between">
            <span className="font-medium text-stone-700">Total Investment</span>
            <span className="text-xl font-bold text-green-700">${formatCurrency(calculateTotal())}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingSection;
